import _ from 'lodash'

export const FEATURE_FLAGS = Object.freeze({
  PAY_NOW_ENABLED: 'PAY_NOW_ENABLED',
  SO_PER_INVOICE_CASHFLOW_ENABLED: 'SO_PER_INVOICE_CASHFLOW_ENABLED',
  CASH_ADVANCE_INTEREST_ENABLED: 'CASH_ADVANCE_INTEREST_ENABLED',
  CASH_ADVANCES_ENABLED: 'CASH_ADVANCES_ENABLED',
  MASTER_LOTS_ENABLED: 'MASTER_LOTS_ENABLED',
  // @provisional — dependent capability of the master-lot builder
  MASTER_LOT_DEMO_ALLOWED: 'MASTER_LOT_DEMO_ALLOWED',
  SETTLEMENT_METALS_TAG_WEIGHT_ENABLED: 'SETTLEMENT_METALS_TAG_WEIGHT_ENABLED',
  REFINERY_LOCALIZED_EXPOSURE_LIMIT_ENABLED: 'REFINERY_LOCALIZED_EXPOSURE_LIMIT_ENABLED',
  PRE_SETTLEMENT_ENABLED: 'PRE_SETTLEMENT_ENABLED',
  BULLION_WALLET_ACCOUNT_SUMMARY_ENABLED: 'BULLION_WALLET_ACCOUNT_SUMMARY_ENABLED',
  BULLION_WALLET_CSV_EXPORT_ENABLED: 'BULLION_WALLET_CSV_EXPORT_ENABLED',
  REFINERY_LOT_PINS_DISTINCT: 'REFINERY_LOT_PINS_DISTINCT',
  REFINERY_NS_SERIALIZED_METALS: 'REFINERY_NS_SERIALIZED_METALS',
  NS_SEMAPHORE_ENABLED: 'NS_SEMAPHORE_ENABLED',
})

export const FEATURE_FLAG_DEPENDENCIES = Object.freeze({
  [FEATURE_FLAGS.BULLION_WALLET_CSV_EXPORT_ENABLED]: FEATURE_FLAGS.BULLION_WALLET_ACCOUNT_SUMMARY_ENABLED,
  [FEATURE_FLAGS.REFINERY_NS_SERIALIZED_METALS]: FEATURE_FLAGS.REFINERY_LOT_PINS_DISTINCT,
  // @provisional
  [FEATURE_FLAGS.MASTER_LOT_DEMO_ALLOWED]: FEATURE_FLAGS.MASTER_LOTS_ENABLED,
})

export const FEATURE_FLAG_METADATA = Object.freeze([
  {
    key: FEATURE_FLAGS.PAY_NOW_ENABLED,
    label: 'Pay Now',
    description: 'Enables Pay Now ACH payment flows for invoices and sales orders.',
    version: '1.94.0',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.SO_PER_INVOICE_CASHFLOW_ENABLED,
    label: 'SO Per-Invoice Cashflow',
    description: 'Breaks an SO\'s cashflow down per invoice, with plain customer-facing labels and statuses. Payment, deposit, and credit-memo rows are clickable, summarizing how each is applied across orders (if relevant); partial applications are flagged inline where only part of a transaction lands on this order. The goal: every invoice, payment, deposit, and credit memo reconciles to the remaining balance.',
    version: '1.97.5',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.MASTER_LOTS_ENABLED,
    label: 'Master Lot Builder',
    description: 'Product-on switch for the Upstream Master Lot Builder. Routes are also admin-gated; flip on to expose `/refinery/master-lots`.',
    version: '1.100.6',
    isPilotable: false,
  },
  {
    key: FEATURE_FLAGS.MASTER_LOT_DEMO_ALLOWED,
    label: 'Master Lot Demo Allowed',
    description: 'Allows demo-mode master lot workflows when the Master Lot Builder is on.',
    version: '1.100.6',
    // @provisional
    isPilotable: false,
  },
  {
    key: FEATURE_FLAGS.SETTLEMENT_METALS_TAG_WEIGHT_ENABLED,
    label: 'Settlement Metals Tag = Settlement Weight',
    description: 'Sends the settlement weight (fine metal oz) as the NetSuite settlement `metals` tag quantity instead of the legacy after-melt net weight. Off keeps the prior netWeightToz behavior.',
    version: '1.98.0',
    isPilotable: false,
  },
  {
    key: FEATURE_FLAGS.REFINERY_LOCALIZED_EXPOSURE_LIMIT_ENABLED,
    label: 'Refinery Localized Exposure Limit',
    description: 'Enables customer-specific USD exposure limits with spot-price metal conversion in refinery validation.',
    version: '1.95.0',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.CASH_ADVANCES_ENABLED,
    label: 'Cash Advances',
    description: 'Enables cash advance requests at lot submission and the Cash Advances page for allowlisted companies. When OFF globally, only allowlisted companies may request advances; admins retain override via admin mode.',
    version: '1.98.3',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.CASH_ADVANCE_INTEREST_ENABLED,
    label: 'Cash Advance Interest Charges',
    description: 'Calculates and applies simple interest on cash advances at settlement time, based on contract rate and days elapsed.',
    version: '1.98.3',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.PRE_SETTLEMENT_ENABLED,
    label: 'Pre-Settlement Payment Program',
    description: 'Enables the Pre-Settlement Payment Program: fee previews on sell tickets, Resources DocuSign enrollment, eligible payment-method visibility, and the trade-ticket / PO terms gate. Keep OFF until the DocuSign template is configured in each environment.',
    version: '1.98.0',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.BULLION_WALLET_ACCOUNT_SUMMARY_ENABLED,
    label: 'Bullion Wallet Account Summary',
    description: 'Replaces legacy bullion balances with the account-summary dashboard, per-metal wallet detail pages, and lock-backed display.',
    version: '1.100.0',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.BULLION_WALLET_CSV_EXPORT_ENABLED,
    label: 'Bullion Wallet CSV Export',
    description: 'Enables CSV download of the bullion wallet account summary and per-metal wallet detail pages. Requires Bullion Wallet Account Summary.',
    version: '1.100.0',
    isPilotable: true,
  },
  {
    key: FEATURE_FLAGS.REFINERY_LOT_PINS_DISTINCT,
    label: 'Distinct Refinery Lot Pins',
    description: 'Uses the native bars-and-pins refinery API contract. Off preserves the legacy bar-embedded pin contract.',
    version: '1.100.3',
    isPilotable: false,
  },
  {
    key: FEATURE_FLAGS.REFINERY_NS_SERIALIZED_METALS,
    label: 'NetSuite Serialized Metals',
    description: 'When on, melt and settlement also send NetSuite a materials map of individual bar and pin items with precious-metal contents. Aggregate metal totals stay the same either way. NetSuite chooses which to use per lot. Requires Distinct Refinery Lot Pins.',
    version: '1.100.3',
    isPilotable: false,
  },
  {
    key: FEATURE_FLAGS.NS_SEMAPHORE_ENABLED,
    label: 'NetSuite Concurrency Semaphore',
    description: 'Coordinates NetSuite request concurrency across API and worker processes via a Redis semaphore with reserved user-facing capacity. Off reverts to the legacy uncoordinated request path. Acts as a hot kill switch: toggle off to immediately stop capacity gating without a redeploy. This flag is process-wide only.',
    version: '1.100.4',
    isPilotable: false,
  },
])

export function isFeatureFlagPilotable(key) {
  const meta = FEATURE_FLAG_METADATA.find((flag) => flag.key === key)
  // absent / undefined / false ⇒ not pilotable
  return meta?.isPilotable === true
}

export function isFeatureFlagEnabled(store, key, resolved = new Set()) {
  if (resolved.has(key)) {
    return false
  }
  resolved.add(key)

  const selectedCompanyId = store.getters.getSelectedCompany && store.getters.getSelectedCompany.id
  const value = store.getters.getConfig(key)
  let enabled
  if (_.isPlainObject(value)) {
    const globallyEnabled = value.enabled === true || value.enabled === 'true'
    if (globallyEnabled) {
      enabled = true
    } else if (isFeatureFlagPilotable(key)) {
      const companyIds = Array.isArray(value.companyIds) ? value.companyIds.map(String) : []
      enabled = Boolean(selectedCompanyId && companyIds.includes(String(selectedCompanyId)))
    } else {
      enabled = false
    }
  } else {
    enabled = value === true || value === 'true'
  }

  const dependency = FEATURE_FLAG_DEPENDENCIES[key]
  if (dependency) {
    return enabled && isFeatureFlagEnabled(store, dependency, resolved)
  }
  return enabled
}

export default {
  computed: {
    featureFlags() {
      return Object.values(FEATURE_FLAGS).reduce((flags, key) => {
        flags[key] = isFeatureFlagEnabled(this.$store, key)
        return flags
      }, {})
    },
  },
}
